import React, { useCallback, useEffect, useState } from 'react';
import { Card, Alert, Button, Descriptions, Space, Tag, Typography, Steps, message } from 'antd';
import { ReloadOutlined, SendOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, FileProtectOutlined } from '@ant-design/icons';
import { getESignWebhookHealth, sendESignWebhookTest } from '../api';

const { Text, Paragraph } = Typography;
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const OUTCOME_LABEL = { bad_mac: 'MAC mismatch (check Private Salt)', bad_payload: 'Malformed payload', failed: 'Processing error' };

/**
 * API Configuration → Webhook → Leegality eSign.
 * Leegality takes webhook + redirect URLs from the WORKFLOW's invitee settings
 * (not from the API request), so this card lists exactly what to paste there,
 * checks the URLs really reach this backend, and runs a MAC-signed self-test.
 */
const ESignWebhookCard = ({ canEdit }) => {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setHealth(await getESignWebhookHealth());
        } catch (e) {
            message.error('Failed to load eSign webhook status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await sendESignWebhookTest();
            setTestResult(r);
            if (r.ok) message.success(`eSign webhook reachable and MAC verified (${r.ms} ms)`);
            else message.error(r.message || `Test failed (HTTP ${r.status || 'no response'})`);
            load();
        } catch (e) {
            const msg = e.response?.data?.message || e.message;
            setTestResult({ ok: false, message: msg });
            message.error(msg);
        } finally {
            setTesting(false);
        }
    };

    const h = health || {};
    const ready = h.saltConfigured && h.httpsReady;
    const tag = () => {
        if (!h.httpsReady) return <Tag color="error" icon={<CloseCircleOutlined />}>Webhook Base URL must be HTTPS</Tag>;
        if (h.returnReachable === false) return <Tag color="error" icon={<CloseCircleOutlined />}>URL does not reach the CRM backend</Tag>;
        if (!h.saltConfigured) return <Tag color="warning" icon={<WarningOutlined />}>Leegality Private Salt not saved — webhooks rejected</Tag>;
        if (h.lastValidAt) return <Tag color="success" icon={<CheckCircleOutlined />}>Receiving webhooks</Tag>;
        if (h.lastTestAt) return <Tag color="processing">Self-test passed — waiting for first Leegality event</Tag>;
        return <Tag>Not tested yet</Tag>;
    };

    const copy = (v) => <Text copyable={{ text: v }} code>{v || '—'}</Text>;

    return (
        <Card
            style={{ marginTop: 24 }}
            title={<Space><FileProtectOutlined />Leegality eSign — Webhook &amp; Redirect</Space>}
            extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} size="small">Refresh</Button>}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>{tag()}</div>
                {h.returnReachable === false && (
                    <Alert type="error" showIcon message="Signers will land on a blank page and webhooks will be lost"
                        description={h.returnProblem || 'The URL is not served by the CRM backend. Fix the Webhook Base URL above.'} />
                )}

                <Descriptions bordered size="small" column={1} title="Paste these on the workflow invitee in Leegality">
                    <Descriptions.Item label="Webhook">{copy(h.webhookUrl)}</Descriptions.Item>
                    <Descriptions.Item label="Error Webhook">{copy(h.errorWebhookUrl)}</Descriptions.Item>
                    <Descriptions.Item label="Redirect URL">{copy(h.redirectUrl)}</Descriptions.Item>
                    <Descriptions.Item label="Base URL (failure fallback)">{copy(h.redirectUrl ? `${h.redirectUrl}?outcome=failed` : '')}</Descriptions.Item>
                    <Descriptions.Item label="Webhook Version"><Text code>v2.5</Text></Descriptions.Item>
                </Descriptions>

                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="Last valid webhook">{fmt(h.lastValidAt)}</Descriptions.Item>
                    <Descriptions.Item label="Last signer return">{fmt(h.lastReturnAt)}</Descriptions.Item>
                    <Descriptions.Item label="Last rejected delivery">
                        {h.lastRejected ? <Text type="danger">{fmt(h.lastRejected.at)} — {OUTCOME_LABEL[h.lastRejected.outcome] || h.lastRejected.outcome}</Text> : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last self-test">{fmt(h.lastTestAt)}</Descriptions.Item>
                    <Descriptions.Item label="Pending > 1 hour (7d)">
                        <Text type={h.last7Days?.pendingOverAnHour ? 'danger' : undefined}>{h.last7Days?.pendingOverAnHour ?? 0}</Text>
                    </Descriptions.Item>
                </Descriptions>

                <Space wrap>
                    <Button type="primary" icon={<SendOutlined />} onClick={runTest} loading={testing} disabled={!canEdit || !ready}>
                        Send signed test webhook
                    </Button>
                    {!ready && <Text type="secondary">Needs an HTTPS Webhook Base URL and the Leegality Private Salt (Leegality tab).</Text>}
                </Space>
                {testResult && (
                    <Alert
                        type={testResult.ok ? 'success' : 'error'}
                        showIcon
                        message={testResult.ok
                            ? `Delivered to ${testResult.webhookUrl} and MAC verified in ${testResult.ms} ms.`
                            : `Test failed${testResult.status ? ` (HTTP ${testResult.status})` : ''}: ${testResult.message || 'no response'}`}
                    />
                )}

                <Alert
                    type="info"
                    showIcon
                    message="Set up in the Leegality dashboard"
                    description={(
                        <Steps
                            direction="vertical"
                            size="small"
                            current={-1}
                            items={[
                                { title: 'Workflows → open the workflow used by the CRM (its Profile ID is in the Leegality tab) → Edit' },
                                { title: 'Invitee → Settings / Invitee Level Options → Custom URL & Webhook' },
                                { title: 'Paste Webhook, Error Webhook, Redirect URL and Base URL from above; Webhook Version v2.5' },
                                { title: 'If offered, allow the sender to change URLs while running (lets the CRM add its per-request reference)' },
                                { title: 'Save / publish the workflow, then press “Send signed test webhook” here.' },
                            ]}
                        />
                    )}
                />
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    The CRM no longer needs a “Signing Complete” click: the webhook (or the signer landing on the Redirect URL)
                    updates the case and the open screen automatically. “Check status now” stays as a manual fallback.
                </Paragraph>
            </Space>
        </Card>
    );
};

export default ESignWebhookCard;
